// ⚡ VERSION 3.0.0 - EDITABLE ROLE DROPDOWN FIXED - CACHE BUSTER ⚡
// If you see this comment in browser console, the new code is loaded!
console.log('🚀 UsersTable v3.0.0 with Editable Role Dropdown FIXED loaded!');

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type User, type UserRole, ROLE_LABELS } from '@/types/user';
import { useCompaniesList } from '@/hooks/useCompaniesData';
import { format } from 'date-fns';
import { MoreVertical, Eye, Plug, Building2, Loader2 } from 'lucide-react';
import { useState } from 'react';

interface UsersTableProps {
  users: User[];
  onViewUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onManageIntegrations: (user: User) => void;
  onManageCompanies: (user: User) => void;
  onUpdateUserRole?: (userId: string, newRole: UserRole) => Promise<void>;
  currentUserId?: string;
  canManageUsers?: boolean;
}

export function UsersTable({ 
  users, 
  onViewUser, 
  onManageIntegrations,
  onManageCompanies,
  onUpdateUserRole,
  currentUserId,
  canManageUsers = false
}: UsersTableProps) {
  const { data: companies = [] } = useCompaniesList();
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(null);
  
  // Debug logging
  console.log('UsersTable props:', {
    onUpdateUserRole: !!onUpdateUserRole,
    canManageUsers,
    currentUserId,
    usersCount: users.length
  });
  
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'default';
      case 'user':
        return 'secondary';

      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    return status === 'active' ? 'default' : 'secondary';
  };

  const getUserCompanies = (companyIds: string[]) => {
    return companies.filter(c => companyIds.includes(c.id));
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    if (!onUpdateUserRole) return;
    
    setUpdatingRoleUserId(userId);
    try {
      await onUpdateUserRole(userId, newRole);
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const isRoleEditable = (user: User) => {
    // Can't edit if no update handler provided
    if (!onUpdateUserRole) {
      console.log('isRoleEditable: No update handler provided');
      return false;
    }
    
    // Can't edit if user doesn't have permission
    if (!canManageUsers) {
      console.log('isRoleEditable: User does not have permission to manage users');
      return false;
    }
    
    // Can't edit own role (if currentUserId is provided)
    if (currentUserId && user.id === currentUserId) {
      console.log('isRoleEditable: Cannot edit own role');
      return false;
    }
    
    console.log('isRoleEditable: Role is editable for user', user.email);
    return true;
  };

  const getIntegrationStatusBadge = (user: User) => {
    const isConnected = !!user.evoUserId;
    
    if (isConnected) {
      return (
        <Badge 
          className="bg-green-500/10 text-green-500 hover:bg-green-500/20 cursor-pointer transition-colors"
          onClick={() => onManageIntegrations(user)}
        >
          <Plug className="h-3 w-3 mr-1" />Connected
        </Badge>
      );
    }
    
    return (
      <Badge 
        variant="outline" 
        className="cursor-pointer hover:bg-accent transition-colors"
        onClick={() => onManageIntegrations(user)}
      >
        <Plug className="h-3 w-3 mr-1" />Not Connected
      </Badge>
    );
  };

  if (users.length === 0) {
    return (
      <div className="border rounded-lg p-12 text-center">
        <p className="text-muted-foreground">No users found</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Companies</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const userCompanies = getUserCompanies(user.companies);
            
            return (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                      <AvatarFallback>
                        {getInitials(user.firstName, user.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="font-medium">{user.firstName} {user.lastName}</div>
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {isRoleEditable(user) ? (
                    updatingRoleUserId === user.id ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span className="text-sm">Updating...</span>
                      </div>
                    ) : (
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.id, value as UserRole)}
                      >
                        <SelectTrigger className="w-[160px] h-8">
                          <SelectValue>
                            <Badge variant={getRoleBadgeVariant(user.role)} className="pointer-events-none">
                              {ROLE_LABELS[user.role]}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="administrator">
                            <Badge variant="default" className="pointer-events-none">
                              {ROLE_LABELS.administrator}
                            </Badge>
                          </SelectItem>
                          <SelectItem value="user">
                            <Badge variant="secondary" className="pointer-events-none">
                              {ROLE_LABELS.user}
                            </Badge>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )
                  ) : (
                    <Badge variant={getRoleBadgeVariant(user.role)}>
                      {ROLE_LABELS[user.role]}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(user.status)}>
                    {user.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {userCompanies.length > 0 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-sm font-medium">
                            {userCompanies.length} {userCompanies.length === 1 ? 'company' : 'companies'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="space-y-1">
                            {userCompanies.map((company) => (
                              <div key={company.id}>{company.name}</div>
                            ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className="text-sm text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell>
                  {user.lastLogin ? (
                    <span className="text-sm">
                      {format(new Date(user.lastLogin), 'MMM d, yyyy')}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">Never</span>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewUser(user)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onManageCompanies(user)}>
                        <Building2 className="w-4 h-4 mr-2" />
                        Manage Companies
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
